import React, { useState, useEffect } from 'react';

/**
 * MiqIntakeForm - Deep Personalization Wizard for MIQ
 * Multi-question form that captures user preferences for AI personalization
 * Supports pre-filling from saved data for editing individual fields
 */
const MiqIntakeForm = ({ 
  options, 
  userId, 
  onComplete, 
  onClose,
  BACKEND_URL,
  userProfile, // User profile data for showing current weight
  savedIntakeData // Previously saved intake data for pre-filling
}) => {
  // Initialize with saved data or empty defaults
  const getInitialData = () => {
    const savedData = savedIntakeData?.intake_data || savedIntakeData || {};
    const useImperial = userProfile?.use_imperial === true;
    
    // Convert target_weight_kg from saved data to user's unit preference
    let targetWeight = savedData.target_weight || null;
    if (!targetWeight && savedData.target_weight_kg) {
      targetWeight = useImperial 
        ? Math.round(savedData.target_weight_kg * 2.205) // kg to lbs
        : savedData.target_weight_kg;
    }
    
    return {
      // Primary goal emphasis
      goal_emphasis: savedData.goal_emphasis || null,
      // Target weight (in user's preferred unit)
      target_weight: targetWeight,
      // Goal timeline in weeks
      goal_timeline_weeks: savedData.goal_timeline_weeks || null,
      // Custom timeline weeks
      custom_timeline_weeks: savedData.custom_timeline_weeks || null,
      // Eating schedule type (standard, intermittent_fasting, custom)
      eating_schedule_type: savedData.eating_schedule_type || null,
      // IF preferences (for intermittent_fasting type)
      if_preference: savedData.if_preference || null,
      // Eating window start time (for IF users)
      eating_window_start: savedData.eating_window_start || null,
      // Custom reset time (for custom schedule users)
      custom_reset_time: savedData.custom_reset_time || null,
      // Calculated calorie reset time (stored for backend use)
      calorie_reset_time: savedData.calorie_reset_time || '00:00',
      // Meals per day
      meals_per_day: savedData.meals_per_day || null,
      // Custom meals count
      custom_meals_count: savedData.custom_meals_count || null,
      // Training vs rest day differences
      training_rest_difference: savedData.training_rest_difference || null,
      // Flexible Macro Mode
      flexible_macro_mode: savedData.flexible_macro_mode || null,
      // Sleep & Stress
      sleep_quality: savedData.sleep_quality || null,
      stress_level: savedData.stress_level || null,
      // Activity style
      activity_style: savedData.activity_style || null,
      // Body type / somatotype
      body_type: savedData.body_type || null,
      // Work schedule pattern
      work_schedule_type: savedData.work_schedule_type || null,
      // Ancestry (OPTIONAL)
      ancestry: savedData.ancestry || null,
      // Medical conditions (OPTIONAL)
      medical_conditions: savedData.medical_conditions || [],
      // Medications (OPTIONAL)
      medications: savedData.medications || [],
      // Supplements (OPTIONAL)
      supplements: savedData.supplements || [],
      // Peptides (OPTIONAL)
      peptides: savedData.peptides || [],
      // Injuries & Mobility (OPTIONAL)
      injuries: savedData.injuries || [],
      injury_details: savedData.injury_details || '',
      upcoming_surgery: savedData.upcoming_surgery || '',
      // Food allergies (OPTIONAL)
      food_allergies: savedData.food_allergies || []
    };
  };

  const [intakeData, setIntakeData] = useState(getInitialData);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [saving, setSaving] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [calculatedGoals, setCalculatedGoals] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Local unit preference state - allows user to toggle in the form
  // Defaults to profile setting, but can be changed
  const [useImperialLocal, setUseImperialLocal] = useState(() => {
    return userProfile?.use_imperial === true;
  });
  
  // Update local imperial setting when profile changes
  useEffect(() => {
    setUseImperialLocal(userProfile?.use_imperial === true);
  }, [userProfile?.use_imperial]);
  
  // Update intakeData when savedIntakeData changes (for editing)
  useEffect(() => {
    const savedData = savedIntakeData?.intake_data || savedIntakeData || {};
    const hasSavedData = Object.values(savedData).some(v => v !== null && v !== undefined);
    
    if (hasSavedData) {
      setIsEditMode(true);
      
      // Convert target_weight_kg to user's preferred unit
      const useImperial = userProfile?.use_imperial === true;
      let targetWeight = savedData.target_weight || null;
      if (!targetWeight && savedData.target_weight_kg) {
        targetWeight = useImperial 
          ? Math.round(savedData.target_weight_kg * 2.205) // kg to lbs
          : savedData.target_weight_kg;
      }
      
      // Update state with saved values
      setIntakeData(prev => ({
        ...prev,
        goal_emphasis: savedData.goal_emphasis || prev.goal_emphasis,
        target_weight: targetWeight || prev.target_weight,
        goal_timeline_weeks: savedData.goal_timeline_weeks || prev.goal_timeline_weeks,
        custom_timeline_weeks: savedData.custom_timeline_weeks || prev.custom_timeline_weeks,
        if_preference: savedData.if_preference || prev.if_preference,
        eating_window: savedData.eating_window || prev.eating_window,
        meals_per_day: savedData.meals_per_day || prev.meals_per_day,
        custom_meals_count: savedData.custom_meals_count || prev.custom_meals_count,
        training_rest_difference: savedData.training_rest_difference || prev.training_rest_difference,
        flexible_macro_mode: savedData.flexible_macro_mode || prev.flexible_macro_mode,
        sleep_quality: savedData.sleep_quality || prev.sleep_quality,
        stress_level: savedData.stress_level || prev.stress_level,
        activity_style: savedData.activity_style || prev.activity_style,
        body_type: savedData.body_type || prev.body_type,
        work_schedule_type: savedData.work_schedule_type || prev.work_schedule_type,
        ancestry: savedData.ancestry || prev.ancestry,
        medical_conditions: savedData.medical_conditions || prev.medical_conditions,
        medications: savedData.medications || prev.medications,
        supplements: savedData.supplements || prev.supplements,
        peptides: savedData.peptides || prev.peptides,
        food_allergies: savedData.food_allergies || prev.food_allergies
      }));
      console.log('[MiqIntakeForm] Pre-filled with saved data:', savedData);
    }
  }, [savedIntakeData, userProfile?.use_imperial]);
  
  // All MIQ intake questions - deep personalization
  const allQuestions = [
    {
      key: 'goal_emphasis',
      question: 'What is your primary goal emphasis?',
      helper: 'This helps MIQ prioritize your nutrition strategy',
      options: [
        { value: 'fat_loss', label: 'Fat Loss', description: 'Prioritize losing body fat while preserving muscle' },
        { value: 'muscle_gain', label: 'Muscle Gain', description: 'Focus on building lean muscle mass' },
        { value: 'recomp', label: 'Body Recomposition', description: 'Lose fat and gain muscle simultaneously' },
        { value: 'metabolic_health', label: 'Metabolic Health', description: 'Optimize blood sugar, energy, and overall health' },
        { value: 'longevity', label: 'Longevity', description: 'Long-term health and healthy aging' },
        { value: 'performance_bulking', label: 'Performance / Physique (Bulking)', description: 'High-carb muscle building phase for athletes' },
        { value: 'performance_cutting', label: 'Performance / Physique (Cutting)', description: 'Aggressive fat loss while preserving muscle' }
      ]
    },
    {
      key: 'target_weight',
      question: 'What is your target weight?',
      helper: 'This helps MIQ calculate your daily calorie deficit or surplus',
      inputType: 'number',
      inputLabel: 'Enter target weight',
      inputUnit: 'auto', // Will be set dynamically based on user profile
      inputMin: 30, // Allow for kg values (30kg minimum)
      inputMax: 400, // Allow for both kg and lbs max
      showCurrentWeight: true,
      optional: true, // User can skip this question
      conditionalShow: (answers) => ['fat_loss', 'muscle_gain', 'recomp', 'performance_bulking', 'performance_cutting'].includes(answers.goal_emphasis)
    },
    {
      key: 'goal_timeline_weeks',
      question: 'What is your target timeline to achieve your goal?',
      helper: 'MIQ uses this to calculate your daily calorie targets. Shorter timelines = larger daily adjustments.',
      options: [
        { value: '4', label: '4 weeks', description: 'Aggressive - requires strong discipline' },
        { value: '8', label: '8 weeks', description: 'Moderate pace - visible results' },
        { value: '12', label: '12 weeks', description: 'Balanced - sustainable progress' },
        { value: '16', label: '16 weeks', description: 'Gradual - very sustainable' },
        { value: '24', label: '24 weeks', description: 'Long-term - lifestyle change' },
        { value: 'custom', label: 'Custom Timeline', description: 'Specify your own number of weeks' }
      ],
      allowCustomInput: true,
      customInputLabel: 'Enter weeks (1-52):',
      customInputKey: 'custom_timeline_weeks',
      customInputMin: 1,
      customInputMax: 52
    },
    {
      key: 'meals_per_day',
      question: 'How many meals do you typically eat per day?',
      helper: 'This affects how MIQ distributes your daily targets',
      options: [
        { value: '1', label: '1 Meal (OMAD)', description: 'One main meal per day / fasting-friendly' },
        { value: '2', label: '2 Meals', description: 'Two larger meals' },
        { value: '3', label: '3 Meals', description: 'Traditional breakfast, lunch, dinner' },
        { value: '4', label: '4 Meals', description: 'Three main meals + one light meal' },
        { value: '5', label: '5 Meals', description: 'Three main meals + two light meals' },
        { value: 'custom', label: 'Custom (6-8 meals)', description: 'Specify exact number of meals including light meals' }
      ]
    },
    {
      key: 'training_rest_difference',
      question: 'Do you want different targets for training vs rest days?',
      helper: 'Athletes often eat more on workout days',
      options: [
        { value: 'yes', label: 'Yes', description: 'Higher calories/carbs on training days' },
        { value: 'no', label: 'No', description: 'Keep it simple with consistent targets' }
      ]
    },
    {
      key: 'flexible_macro_mode',
      question: 'Enable Flexible Macro Mode?',
      helper: 'Focus on hitting your calories and protein. Carbs and fats stay flexible.',
      premiumFeature: true,
      premiumTier: 'smart',
      premiumBadge: '🎯 Smart Plaite',
      educationalCopy: 'Research shows protein and calories are the most important macros for most goals. Flexible Mode lets you hit those and stress less about carbs and fats.',
      options: [
        { value: 'enabled', label: 'Yes, Enable Flexible Mode', description: 'Track calories + protein only for goal success' },
        { value: 'disabled', label: 'No, Track All Macros', description: 'Traditional tracking of all macros' }
      ]
    },
    {
      key: 'sleep_quality',
      question: 'How is your sleep most nights?',
      helper: 'Sleep affects recovery, hunger hormones, and metabolism',
      options: [
        { value: 'poor', label: 'Poor', description: 'Less than 5 hours or frequently disrupted' },
        { value: 'fair', label: 'Fair', description: '5-6 hours, sometimes disrupted' },
        { value: 'good', label: 'Good', description: '7-8 hours, generally restful' },
        { value: 'excellent', label: 'Excellent', description: '8+ hours, consistently great' }
      ]
    },
    {
      key: 'stress_level',
      question: 'How would you describe your daily stress level?',
      helper: 'Chronic stress affects cortisol, cravings, and fat storage',
      options: [
        { value: 'low', label: 'Low', description: 'Generally relaxed and calm' },
        { value: 'moderate', label: 'Moderate', description: 'Normal work/life stress' },
        { value: 'high', label: 'High', description: 'Frequently stressed or overwhelmed' }
      ]
    },
    {
      key: 'activity_style',
      question: 'What is your primary activity style?',
      helper: 'Different training types have different nutritional needs',
      options: [
        { value: 'yoga_pilates', label: 'Yoga / Pilates / Mobility', description: 'Focus on flexibility and core' },
        { value: 'strength', label: 'Strength / Weights', description: 'Lifting and resistance training' },
        { value: 'boxing_mma', label: 'Boxing / MMA / Combat Sports', description: 'High intensity martial arts' },
        { value: 'endurance', label: 'Endurance / Cardio', description: 'Running, cycling, swimming' },
        { value: 'mixed', label: 'Mixed Training', description: 'Variety of different activities' },
        { value: 'sedentary', label: 'Mostly Sedentary', description: 'Desk job, limited exercise' }
      ]
    },
    {
      key: 'body_type',
      question: 'What best describes your natural body type?',
      helper: 'This helps MIQ understand your metabolism and optimize recommendations. Your body type affects how you respond to different diets.',
      options: [
        { value: 'ectomorph', label: 'Ectomorph', description: 'Naturally lean, fast metabolism, hard to gain weight' },
        { value: 'mesomorph', label: 'Mesomorph', description: 'Athletic build, gains muscle easily, moderate metabolism' },
        { value: 'endomorph', label: 'Endomorph', description: 'Larger frame, slower metabolism, tends to store fat' },
        { value: 'ecto_meso', label: 'Ecto-Mesomorph', description: 'Lean but can build muscle with effort' },
        { value: 'meso_endo', label: 'Meso-Endomorph', description: 'Muscular but tends to gain fat easily' },
        { value: 'not_sure', label: 'Not Sure', description: 'Let MIQ analyze based on other factors' }
      ]
    },
    {
      key: 'work_schedule_type',
      question: 'What is your typical work schedule?',
      helper: 'MIQ uses this to give you personalized meal timing tips based on when you work (not for calorie tracking)',
      optional: true,
      options: [
        { value: 'standard_day', label: 'Standard Day Schedule', description: 'Typical 9-5 or daytime hours' },
        { value: 'night_shifts', label: 'Night Shifts', description: 'Working overnight / graveyard shifts' },
        { value: 'rotating_shifts', label: 'Rotating Shifts', description: 'Shifts change between day/night' },
        { value: 'emergency_oncall', label: 'Emergency / On-Call', description: 'Healthcare, first responders, etc.' },
        { value: 'irregular', label: 'Irregular Schedule', description: 'No consistent pattern / freelance' }
      ]
    },
    {
      key: 'ancestry',
      question: 'Ancestry / Ethnic Background (Optional)',
      helper: 'Some metabolic patterns vary across populations due to genetics, traditional diets, and historical adaptations. This helps MIQ personalize guidance. You can skip or change this anytime.',
      secondaryInfo: 'MIQ uses this only as a supporting signal — never as a diagnosis and never shown in recommendations.',
      optional: true,
      grouped: true,
      groups: [
        {
          label: 'Africa & African Diaspora',
          options: [
            { value: 'west_african', label: 'West African' },
            { value: 'east_african', label: 'East African' },
            { value: 'north_african', label: 'North African' },
            { value: 'central_southern_african', label: 'Central / Southern African' },
            { value: 'afro_caribbean', label: 'Afro-Caribbean' },
            { value: 'african_american', label: 'African American' },
            { value: 'other_african', label: 'Other African / African Diaspora' }
          ]
        },
        {
          label: 'Europe',
          options: [
            { value: 'northern_european', label: 'Northern European (Scandinavian)' },
            { value: 'western_european', label: 'Western European' },
            { value: 'southern_european', label: 'Southern European (Mediterranean)' },
            { value: 'eastern_european', label: 'Eastern European' },
            { value: 'balkan', label: 'Balkan' },
            { value: 'celtic_british', label: 'Celtic / British Isles' },
            { value: 'mixed_european', label: 'Mixed European' }
          ]
        },
        {
          label: 'Middle East & North Africa (MENA)',
          options: [
            { value: 'middle_eastern', label: 'Middle Eastern' },
            { value: 'arab', label: 'Arab' },
            { value: 'persian_iranian', label: 'Persian / Iranian' },
            { value: 'turkish', label: 'Turkish' },
            { value: 'levantine', label: 'Levantine' },
            { value: 'mixed_mena', label: 'Mixed MENA' }
          ]
        },
        {
          label: 'South Asia',
          options: [
            { value: 'indian', label: 'Indian' },
            { value: 'pakistani', label: 'Pakistani' },
            { value: 'bangladeshi', label: 'Bangladeshi' },
            { value: 'sri_lankan', label: 'Sri Lankan' },
            { value: 'nepali', label: 'Nepali' },
            { value: 'other_south_asian', label: 'Other South Asian' },
            { value: 'mixed_south_asian', label: 'Mixed South Asian' }
          ]
        },
        {
          label: 'East Asia',
          options: [
            { value: 'chinese', label: 'Chinese' },
            { value: 'korean', label: 'Korean' },
            { value: 'japanese', label: 'Japanese' },
            { value: 'mongolian', label: 'Mongolian' },
            { value: 'other_east_asian', label: 'Other East Asian' },
            { value: 'mixed_east_asian', label: 'Mixed East Asian' }
          ]
        },
        {
          label: 'Southeast Asia',
          options: [
            { value: 'filipino', label: 'Filipino' },
            { value: 'thai', label: 'Thai' },
            { value: 'vietnamese', label: 'Vietnamese' },
            { value: 'indonesian', label: 'Indonesian' },
            { value: 'malaysian', label: 'Malaysian' },
            { value: 'cambodian_lao_burmese', label: 'Cambodian / Lao / Burmese' },
            { value: 'other_southeast_asian', label: 'Other Southeast Asian' }
          ]
        },
        {
          label: 'Central Asia',
          options: [
            { value: 'kazakh', label: 'Kazakh' },
            { value: 'uzbek', label: 'Uzbek' },
            { value: 'turkmen', label: 'Turkmen' },
            { value: 'kyrgyz', label: 'Kyrgyz' },
            { value: 'tajik', label: 'Tajik' },
            { value: 'other_central_asian', label: 'Other Central Asian' }
          ]
        },
        {
          label: 'Latin America & Caribbean',
          options: [
            { value: 'mexican', label: 'Mexican' },
            { value: 'central_american', label: 'Central American' },
            { value: 'south_american_andean', label: 'South American (Andean)' },
            { value: 'south_american_southern', label: 'South American (Southern Cone)' },
            { value: 'caribbean', label: 'Caribbean' },
            { value: 'mixed_latin_american', label: 'Mixed Latin American' }
          ]
        },
        {
          label: 'Indigenous Peoples',
          options: [
            { value: 'indigenous_north_american', label: 'Indigenous North American' },
            { value: 'indigenous_south_american', label: 'Indigenous South American' },
            { value: 'indigenous_australian', label: 'Indigenous Australian' },
            { value: 'indigenous_arctic_inuit', label: 'Indigenous Arctic / Inuit' },
            { value: 'indigenous_pacific_islander', label: 'Indigenous Pacific Islander' },
            { value: 'other_indigenous', label: 'Other Indigenous' }
          ]
        },
        {
          label: 'Pacific Islander',
          options: [
            { value: 'polynesian', label: 'Polynesian' },
            { value: 'micronesian', label: 'Micronesian' },
            { value: 'melanesian', label: 'Melanesian' },
            { value: 'maori', label: 'Māori' },
            { value: 'other_pacific_islander', label: 'Other Pacific Islander' }
          ]
        },
        {
          label: 'Other / Mixed',
          options: [
            { value: 'mixed_ancestry', label: 'Mixed Ancestry' },
            { value: 'other_self_described', label: 'Other (self-described)' },
            { value: 'prefer_not_to_say', label: 'Prefer not to say' }
          ]
        }
      ]
    },
    {
      key: 'medical_conditions',
      question: 'Do you have any medical conditions we should know about?',
      helper: 'This helps MIQ provide safer, more personalized recommendations. Used for SmartMeals, SmartSwaps, and all AI features.',
      secondaryInfo: 'Your health data is never shared and only used to improve your nutrition guidance.',
      optional: true,
      multiSelect: true,
      grouped: true,
      groups: [
        {
          label: 'Metabolic',
          options: [
            { value: 'type_2_diabetes', label: 'Type 2 Diabetes' },
            { value: 'type_1_diabetes', label: 'Type 1 Diabetes' },
            { value: 'prediabetes', label: 'Prediabetes' },
            { value: 'insulin_resistance', label: 'Insulin Resistance' },
            { value: 'metabolic_syndrome', label: 'Metabolic Syndrome' },
            { value: 'pcos', label: 'PCOS' },
            { value: 'hypothyroidism', label: 'Hypothyroidism' },
            { value: 'hyperthyroidism', label: 'Hyperthyroidism' }
          ]
        },
        {
          label: 'Cardiovascular',
          options: [
            { value: 'high_blood_pressure', label: 'High Blood Pressure' },
            { value: 'heart_disease', label: 'Heart Disease' },
            { value: 'high_cholesterol', label: 'High Cholesterol' },
            { value: 'high_triglycerides', label: 'High Triglycerides' },
            { value: 'atrial_fibrillation', label: 'Atrial Fibrillation' }
          ]
        },
        {
          label: 'Digestive',
          options: [
            { value: 'celiac', label: 'Celiac Disease' },
            { value: 'ibs', label: 'IBS (Irritable Bowel)' },
            { value: 'ibd_crohns', label: 'IBD / Crohns Disease' },
            { value: 'ibd_colitis', label: 'Ulcerative Colitis' },
            { value: 'gerd', label: 'GERD / Acid Reflux' },
            { value: 'gastroparesis', label: 'Gastroparesis' },
            { value: 'diverticulitis', label: 'Diverticulitis' }
          ]
        },
        {
          label: 'Kidney & Liver',
          options: [
            { value: 'kidney_disease', label: 'Kidney Disease (CKD)' },
            { value: 'kidney_stones', label: 'Kidney Stones' },
            { value: 'fatty_liver', label: 'Fatty Liver Disease' },
            { value: 'liver_disease', label: 'Liver Disease' }
          ]
        },
        {
          label: 'Autoimmune & Inflammatory',
          options: [
            { value: 'rheumatoid_arthritis', label: 'Rheumatoid Arthritis' },
            { value: 'lupus', label: 'Lupus' },
            { value: 'hashimotos', label: 'Hashimotos Thyroiditis' },
            { value: 'psoriasis', label: 'Psoriasis' },
            { value: 'multiple_sclerosis', label: 'Multiple Sclerosis' }
          ]
        },
        {
          label: 'Hormonal & Reproductive',
          options: [
            { value: 'menopause', label: 'Menopause / Perimenopause' },
            { value: 'pregnancy', label: 'Pregnant' },
            { value: 'breastfeeding', label: 'Breastfeeding' },
            { value: 'fertility_ivf', label: 'Fertility / IVF Preparation' }
          ]
        },
        {
          label: 'Neurological',
          options: [
            { value: 'migraine', label: 'Migraine' },
            { value: 'adhd_focus', label: 'ADHD / Focus Support' }
          ]
        },
        {
          label: 'Bone, Blood & Other',
          options: [
            { value: 'gout', label: 'Gout' },
            { value: 'osteoporosis', label: 'Osteoporosis' },
            { value: 'anemia', label: 'Anemia' },
            { value: 'asthma', label: 'Asthma' },
            { value: 'eating_disorder_history', label: 'History of Eating Disorder' },
            { value: 'recovering_injury', label: 'Recovering from Injury' },
            { value: 'medication_sensitive', label: 'Medication-Sensitive Nutrition' },
            { value: 'none', label: 'None / Prefer not to say' }
          ]
        }
      ]
    },
    {
      key: 'medications',
      question: 'Are you currently taking any medications?',
      helper: 'MIQ uses this to avoid food-drug interactions and optimize supplement timing around your medications.',
      secondaryInfo: 'This info is private and only used to improve your health intelligence.',
      optional: true,
      multiSelect: true,
      grouped: true,
      groups: [
        {
          label: 'Thyroid',
          options: [
            { value: 'levothyroxine', label: 'Levothyroxine (Synthroid)' },
            { value: 'liothyronine', label: 'Liothyronine (Cytomel)' },
            { value: 'methimazole', label: 'Methimazole (Tapazole)' }
          ]
        },
        {
          label: 'Diabetes & Blood Sugar',
          options: [
            { value: 'metformin', label: 'Metformin' },
            { value: 'insulin', label: 'Insulin' },
            { value: 'glipizide', label: 'Glipizide' },
            { value: 'semaglutide', label: 'Semaglutide (Ozempic/Wegovy)' },
            { value: 'tirzepatide', label: 'Tirzepatide (Mounjaro)' }
          ]
        },
        {
          label: 'Heart & Blood Pressure',
          options: [
            { value: 'lisinopril', label: 'Lisinopril' },
            { value: 'amlodipine', label: 'Amlodipine' },
            { value: 'losartan', label: 'Losartan' },
            { value: 'metoprolol', label: 'Metoprolol' },
            { value: 'atorvastatin', label: 'Atorvastatin (Lipitor)' },
            { value: 'rosuvastatin', label: 'Rosuvastatin (Crestor)' },
            { value: 'warfarin', label: 'Warfarin (Coumadin)' },
            { value: 'aspirin', label: 'Aspirin (daily low-dose)' }
          ]
        },
        {
          label: 'Mental Health',
          options: [
            { value: 'sertraline', label: 'Sertraline (Zoloft)' },
            { value: 'escitalopram', label: 'Escitalopram (Lexapro)' },
            { value: 'fluoxetine', label: 'Fluoxetine (Prozac)' },
            { value: 'bupropion', label: 'Bupropion (Wellbutrin)' },
            { value: 'venlafaxine', label: 'Venlafaxine (Effexor)' },
            { value: 'trazodone', label: 'Trazodone' },
            { value: 'adderall', label: 'Adderall / Amphetamine' }
          ]
        },
        {
          label: 'Pain & Inflammation',
          options: [
            { value: 'ibuprofen', label: 'Ibuprofen (Advil) - regular' },
            { value: 'naproxen', label: 'Naproxen (Aleve) - regular' },
            { value: 'prednisone', label: 'Prednisone' },
            { value: 'gabapentin', label: 'Gabapentin' }
          ]
        },
        {
          label: 'Digestive',
          options: [
            { value: 'omeprazole', label: 'Omeprazole (Prilosec)' },
            { value: 'pantoprazole', label: 'Pantoprazole (Protonix)' },
            { value: 'famotidine', label: 'Famotidine (Pepcid)' }
          ]
        },
        {
          label: 'Hormonal',
          options: [
            { value: 'birth_control', label: 'Birth Control (oral)' },
            { value: 'testosterone', label: 'Testosterone (TRT)' },
            { value: 'estrogen_hrt', label: 'Estrogen / HRT' }
          ]
        },
        {
          label: 'Other',
          options: [
            { value: 'other_medication', label: 'Other (not listed)' },
            { value: 'none', label: 'None / Prefer not to say' }
          ]
        }
      ]
    },
    {
      key: 'supplements',
      question: 'What supplements do you currently take?',
      helper: 'MIQ can suggest optimal timing and flag interactions between your supplements and medications.',
      secondaryInfo: 'This powers the Smart Supplement Timing feature in Health Intelligence.',
      optional: true,
      multiSelect: true,
      grouped: true,
      groups: [
        {
          label: 'Vitamins',
          options: [
            { value: 'vitamin_d', label: 'Vitamin D' },
            { value: 'vitamin_b12', label: 'Vitamin B12' },
            { value: 'vitamin_c', label: 'Vitamin C' },
            { value: 'vitamin_b_complex', label: 'B-Complex' },
            { value: 'vitamin_k2', label: 'Vitamin K2' },
            { value: 'vitamin_e', label: 'Vitamin E' },
            { value: 'folate', label: 'Folate / Folic Acid' },
            { value: 'multivitamin', label: 'Multivitamin' },
            { value: 'prenatal', label: 'Prenatal Vitamin' }
          ]
        },
        {
          label: 'Minerals',
          options: [
            { value: 'calcium', label: 'Calcium' },
            { value: 'magnesium', label: 'Magnesium' },
            { value: 'iron', label: 'Iron' },
            { value: 'zinc', label: 'Zinc' },
            { value: 'selenium', label: 'Selenium' },
            { value: 'potassium', label: 'Potassium' },
            { value: 'chromium', label: 'Chromium' },
            { value: 'iodine', label: 'Iodine' }
          ]
        },
        {
          label: 'Omega & Fats',
          options: [
            { value: 'omega_3', label: 'Omega-3 / Fish Oil' },
            { value: 'cod_liver_oil', label: 'Cod Liver Oil' },
            { value: 'krill_oil', label: 'Krill Oil' },
            { value: 'flaxseed_oil', label: 'Flaxseed Oil' }
          ]
        },
        {
          label: 'Performance & Recovery',
          options: [
            { value: 'creatine', label: 'Creatine' },
            { value: 'protein_powder', label: 'Protein Powder' },
            { value: 'bcaa', label: 'BCAAs' },
            { value: 'collagen', label: 'Collagen' },
            { value: 'glutamine', label: 'L-Glutamine' },
            { value: 'beta_alanine', label: 'Beta-Alanine' },
            { value: 'pre_workout', label: 'Pre-Workout' }
          ]
        },
        {
          label: 'Gut & Digestion',
          options: [
            { value: 'probiotics', label: 'Probiotics' },
            { value: 'fiber_supplement', label: 'Fiber Supplement' },
            { value: 'digestive_enzymes', label: 'Digestive Enzymes' },
            { value: 'apple_cider_vinegar', label: 'Apple Cider Vinegar' }
          ]
        },
        {
          label: 'Herbal & Adaptogens',
          options: [
            { value: 'ashwagandha', label: 'Ashwagandha' },
            { value: 'turmeric', label: 'Turmeric / Curcumin' },
            { value: 'berberine', label: 'Berberine' },
            { value: 'rhodiola', label: 'Rhodiola' },
            { value: 'lions_mane', label: 'Lions Mane' },
            { value: 'milk_thistle', label: 'Milk Thistle' },
            { value: 'saw_palmetto', label: 'Saw Palmetto' },
            { value: 'ginseng', label: 'Ginseng' }
          ]
        },
        {
          label: 'Sleep & Relaxation',
          options: [
            { value: 'melatonin', label: 'Melatonin' },
            { value: 'magnesium_glycinate', label: 'Magnesium Glycinate (sleep)' },
            { value: 'l_theanine', label: 'L-Theanine' },
            { value: 'valerian_root', label: 'Valerian Root' },
            { value: 'gaba', label: 'GABA' }
          ]
        },
        {
          label: 'Other',
          options: [
            { value: 'coq10', label: 'CoQ10' },
            { value: 'glucosamine', label: 'Glucosamine' },
            { value: 'msm', label: 'MSM' },
            { value: 'other_supplement', label: 'Other (not listed)' },
            { value: 'none', label: 'None' }
          ]
        }
      ]
    },
    {
      key: 'peptides',
      question: 'Are you currently using any peptides?',
      helper: 'MIQ can optimize your peptide protocol — dosing, timing, stacking, and interactions with your medications and supplements.',
      secondaryInfo: 'This powers the Peptide Planner and dose calculator in Smart Coach.',
      optional: true,
      multiSelect: true,
      grouped: true,
      groups: [
        {
          label: 'Healing & Recovery',
          options: [
            { value: 'bpc_157', label: 'BPC-157' },
            { value: 'tb_500', label: 'TB-500 (Thymosin Beta-4)' },
            { value: 'bpc_tb_combo', label: 'BPC-157 / TB-500 Combo' },
            { value: 'ghk_cu', label: 'GHK-Cu (Copper Peptide)' },
            { value: 'kpv', label: 'KPV (Gut Healing)' },
            { value: 'll_37', label: 'LL-37 (Antimicrobial)' }
          ]
        },
        {
          label: 'Growth Hormone Secretagogues',
          options: [
            { value: 'cjc_ipamorelin', label: 'CJC-1295 / Ipamorelin' },
            { value: 'cjc_1295_dac', label: 'CJC-1295 DAC' },
            { value: 'tesamorelin', label: 'Tesamorelin' },
            { value: 'sermorelin', label: 'Sermorelin' },
            { value: 'mk_677', label: 'MK-677 (Ibutamoren)' },
            { value: 'ghrp_2', label: 'GHRP-2' },
            { value: 'ghrp_6', label: 'GHRP-6' },
            { value: 'hexarelin', label: 'Hexarelin' }
          ]
        },
        {
          label: 'Fat Loss & Metabolic',
          options: [
            { value: 'aod_9604', label: 'AOD-9604' },
            { value: 'fragment_176_191', label: 'Fragment 176-191' },
            { value: '5_amino_1mq', label: '5-Amino-1MQ' },
            { value: 'mots_c', label: 'MOTS-c' },
            { value: 'tesamorelin_fat', label: 'Tesamorelin (for visceral fat)' }
          ]
        },
        {
          label: 'Cognitive & Mood',
          options: [
            { value: 'selank', label: 'Selank' },
            { value: 'semax', label: 'Semax' },
            { value: 'na_selank', label: 'NA-Selank Amidate' },
            { value: 'na_semax', label: 'NA-Semax Amidate' },
            { value: 'dihexa', label: 'Dihexa' }
          ]
        },
        {
          label: 'Sexual Health & Wellness',
          options: [
            { value: 'pt_141', label: 'PT-141 (Bremelanotide)' },
            { value: 'kisspeptin', label: 'Kisspeptin-10' },
            { value: 'melanotan_2', label: 'Melanotan II' }
          ]
        },
        {
          label: 'Anti-Aging & Longevity',
          options: [
            { value: 'epithalon', label: 'Epithalon (Epitalon)' },
            { value: 'thymalin', label: 'Thymalin' },
            { value: 'thymosin_alpha_1', label: 'Thymosin Alpha-1' },
            { value: 'snap_8', label: 'SNAP-8' },
            { value: 'foxo4_dri', label: 'FOXO4-DRI' }
          ]
        },
        {
          label: 'Muscle & Performance',
          options: [
            { value: 'follistatin_344', label: 'Follistatin 344' },
            { value: 'igf_1_lr3', label: 'IGF-1 LR3' },
            { value: 'mgf', label: 'MGF (Mechano Growth Factor)' },
            { value: 'igf_1_des', label: 'IGF-1 DES' }
          ]
        },
        {
          label: 'Immune & Sleep',
          options: [
            { value: 'dsip', label: 'DSIP (Delta Sleep Inducing)' },
            { value: 'ta1', label: 'TA-1 (Thymosin Alpha-1)' },
            { value: 'ss_31', label: 'SS-31 (Elamipretide)' }
          ]
        },
        {
          label: 'Other',
          options: [
            { value: 'other_peptide', label: 'Other (not listed)' },
            { value: 'none', label: 'None' }
          ]
        }
      ]
    },
    {
      key: 'injuries',
      question: 'Do you have any injuries or mobility limitations?',
      helper: 'MIQ will customize your workout plans to avoid aggravating injuries and include rehabilitation exercises.',
      secondaryInfo: 'Select all that apply. Your workout plans and Smart Coach will factor these in automatically.',
      optional: true,
      multiSelect: true,
      grouped: true,
      groups: [
        {
          label: 'Back & Spine',
          options: [
            { value: 'lower_back_chronic', label: 'Chronic Lower Back Pain' },
            { value: 'lower_back_acute', label: 'Acute Lower Back Injury' },
            { value: 'upper_back', label: 'Upper Back / Thoracic Pain' },
            { value: 'herniated_disc', label: 'Herniated / Bulging Disc' },
            { value: 'sciatica', label: 'Sciatica' },
            { value: 'spinal_stenosis', label: 'Spinal Stenosis' },
            { value: 'scoliosis', label: 'Scoliosis' }
          ]
        },
        {
          label: 'Shoulder & Upper Body',
          options: [
            { value: 'frozen_shoulder_left', label: 'Frozen Shoulder (Left)' },
            { value: 'frozen_shoulder_right', label: 'Frozen Shoulder (Right)' },
            { value: 'rotator_cuff_left', label: 'Rotator Cuff Injury (Left)' },
            { value: 'rotator_cuff_right', label: 'Rotator Cuff Injury (Right)' },
            { value: 'shoulder_impingement', label: 'Shoulder Impingement' },
            { value: 'tennis_elbow', label: 'Tennis Elbow (Lateral Epicondylitis)' },
            { value: 'golfers_elbow', label: 'Golfer\'s Elbow (Medial Epicondylitis)' },
            { value: 'wrist_injury_left', label: 'Wrist / Carpal Tunnel (Left)' },
            { value: 'wrist_injury_right', label: 'Wrist / Carpal Tunnel (Right)' }
          ]
        },
        {
          label: 'Hip & Groin',
          options: [
            { value: 'hip_replacement_left', label: 'Hip Replacement (Left)' },
            { value: 'hip_replacement_right', label: 'Hip Replacement (Right)' },
            { value: 'hip_pending_surgery_left', label: 'Hip — Awaiting Surgery (Left)' },
            { value: 'hip_pending_surgery_right', label: 'Hip — Awaiting Surgery (Right)' },
            { value: 'hip_bursitis', label: 'Hip Bursitis' },
            { value: 'hip_labral_tear', label: 'Hip Labral Tear' },
            { value: 'groin_strain', label: 'Groin / Adductor Strain' },
            { value: 'hip_flexor_strain', label: 'Hip Flexor Strain' }
          ]
        },
        {
          label: 'Knee',
          options: [
            { value: 'knee_replacement_left', label: 'Knee Replacement (Left)' },
            { value: 'knee_replacement_right', label: 'Knee Replacement (Right)' },
            { value: 'acl_tear', label: 'ACL Tear / Reconstruction' },
            { value: 'mcl_injury', label: 'MCL Injury' },
            { value: 'meniscus_tear', label: 'Meniscus Tear' },
            { value: 'patella_tendinitis', label: 'Patella Tendinitis (Jumper\'s Knee)' },
            { value: 'runners_knee', label: 'Runner\'s Knee (ITBS)' },
            { value: 'knee_arthritis', label: 'Knee Arthritis / Osteoarthritis' }
          ]
        },
        {
          label: 'Ankle & Foot',
          options: [
            { value: 'ankle_sprain_left', label: 'Ankle Sprain (Left)' },
            { value: 'ankle_sprain_right', label: 'Ankle Sprain (Right)' },
            { value: 'achilles_tendinitis', label: 'Achilles Tendinitis' },
            { value: 'plantar_fasciitis', label: 'Plantar Fasciitis' },
            { value: 'ankle_instability', label: 'Chronic Ankle Instability' },
            { value: 'stress_fracture_foot', label: 'Stress Fracture (Foot)' }
          ]
        },
        {
          label: 'Other',
          options: [
            { value: 'neck_pain', label: 'Chronic Neck Pain' },
            { value: 'whiplash', label: 'Whiplash / Cervical Strain' },
            { value: 'rib_injury', label: 'Rib Injury / Costochondritis' },
            { value: 'hernia', label: 'Hernia (Inguinal/Abdominal)' },
            { value: 'fibromyalgia', label: 'Fibromyalgia' },
            { value: 'arthritis_general', label: 'General Arthritis / Rheumatoid' },
            { value: 'post_surgery_recovery', label: 'Post-Surgery Recovery (General)' },
            { value: 'other_injury', label: 'Other (not listed)' },
            { value: 'none', label: 'None' }
          ]
        }
      ],
      // Additional text input for details
      additionalInputs: [
        {
          key: 'injury_details',
          label: 'Additional injury details (optional)',
          placeholder: 'e.g., "Right hip replaced 2024, left hip surgery April 7, 2026. Chronic lower back from years of heavy lifting."',
          type: 'textarea'
        },
        {
          key: 'upcoming_surgery',
          label: 'Upcoming surgery date (optional)',
          placeholder: 'e.g., "Left hip replacement - April 7, 2026"',
          type: 'text'
        }
      ]
    },
    {
      key: 'food_allergies',
      question: 'Do you have any food allergies or intolerances?',
      helper: 'MIQ will avoid recommending foods that could trigger reactions. Used across all Smart features.',
      secondaryInfo: 'Select all that apply, or tap "No Allergies" to continue.',
      optional: true,
      multiSelect: true,
      grouped: true,
      hasNoneOption: true,
      groups: [
        {
          label: 'Allergies',
          options: [
            { value: 'tree_nuts', label: 'Tree Nut Allergy' },
            { value: 'peanuts', label: 'Peanut Allergy' },
            { value: 'shellfish', label: 'Shellfish Allergy' },
            { value: 'eggs', label: 'Egg Allergy' },
            { value: 'soy', label: 'Soy Allergy' },
            { value: 'milk', label: 'Dairy Allergy' },
            { value: 'wheat', label: 'Wheat Allergy' },
            { value: 'fish', label: 'Fish Allergy' },
            { value: 'sesame', label: 'Sesame Allergy' }
          ]
        },
        {
          label: 'Intolerances',
          options: [
            { value: 'gluten_sensitive', label: 'Gluten Intolerance' },
            { value: 'lactose', label: 'Lactose Intolerance' },
            { value: 'fructose', label: 'Fructose Intolerance' },
            { value: 'histamine', label: 'Histamine Intolerance' },
            { value: 'fodmap', label: 'FODMAP Sensitive' },
            { value: 'sulfite', label: 'Sulfite Sensitivity' }
          ]
        },
        {
          label: 'Other',
          options: [
            { value: 'corn', label: 'Corn' },
            { value: 'nightshades', label: 'Nightshades (tomatoes, peppers)' },
            { value: 'citrus', label: 'Citrus' },
            { value: 'red_meat', label: 'Red Meat (Alpha-gal)' },
            { value: 'other_allergy', label: 'Other (not listed)' }
          ]
        }
      ]
    }
  ];
  
  // Filter questions based on conditionalShow
  const activeQuestions = allQuestions.filter(q => {
    if (q.conditionalShow) {
      return q.conditionalShow(intakeData);
    }
    return true;
  });
  
  const currentConfig = activeQuestions[currentQuestion];
  const currentKey = currentConfig?.key;
  
  const handleSelect = (value) => {
    if (currentConfig.multiSelect) {
      const currentValues = intakeData[currentKey] || [];
      // "None" is mutually exclusive with all other options
      if (value === 'none') {
        // Toggle none: if already selected, deselect; otherwise select only 'none'
        const newValues = currentValues.includes('none') ? [] : ['none'];
        setIntakeData({ ...intakeData, [currentKey]: newValues });
      } else {
        // Selecting any allergy removes 'none'
        const withoutNone = currentValues.filter(v => v !== 'none');
        const newValues = withoutNone.includes(value)
          ? withoutNone.filter(v => v !== value)
          : [...withoutNone, value];
        setIntakeData({ ...intakeData, [currentKey]: newValues });
      }
    } else {
      setIntakeData({ ...intakeData, [currentKey]: value });
    }
  };
  
  const handleNext = async () => {
    if (currentQuestion < activeQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Save all intake data
      setSaving(true);
      try {
        // Transform intake data to match backend model
        // Use the LOCAL imperial state (what user selected in the form)
        const transformedData = { ...intakeData };
        
        // Convert target_weight to target_weight_kg for backend
        if (transformedData.target_weight) {
          transformedData.target_weight_kg = useImperialLocal 
            ? Math.round(transformedData.target_weight / 2.205 * 10) / 10 // lbs to kg
            : transformedData.target_weight; // already in kg
          delete transformedData.target_weight; // Remove old key
        }
        
        // Calculate calorie_reset_time based on eating schedule
        let calorieResetTime = '00:00'; // Default midnight
        
        if (transformedData.eating_schedule_type === 'standard') {
          calorieResetTime = '00:00'; // Midnight reset
        } else if (transformedData.eating_schedule_type === 'custom') {
          calorieResetTime = transformedData.custom_reset_time || '00:00';
        } else if (transformedData.eating_schedule_type === 'intermittent_fasting') {
          // Calculate end of eating window based on IF protocol and start time
          const ifProtocol = transformedData.if_preference;
          const startTime = transformedData.eating_window_start || '12:00';
          
          // Parse hours from IF protocol (e.g., '16_8' = 8 hour eating window)
          const eatingHours = {
            '12_12': 12, '14_10': 10, '16_8': 8, '18_6': 6, '20_4': 4
          }[ifProtocol] || 8;
          
          // Calculate end time
          const [startHour] = startTime.split(':').map(Number);
          const endHour = (startHour + eatingHours) % 24;
          calorieResetTime = `${endHour.toString().padStart(2, '0')}:00`;
        }
        
        transformedData.calorie_reset_time = calorieResetTime;
        
        const response = await fetch(`${BACKEND_URL}/api/ai-nutrition-goals/${userId}/intake`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(transformedData)
        });
        if (response.ok) {
          const data = await response.json();
          // Store calculated goals for summary display
          setCalculatedGoals(data);
          // Show completion summary instead of immediately closing
          setShowSummary(true);
        } else {
          const errorData = await response.json();
          console.error('Error saving MIQ intake:', errorData);
          alert('Failed to save. Please try again.');
        }
      } catch (error) {
        console.error('Error saving MIQ intake:', error);
        alert('Network error. Please try again.');
      } finally {
        setSaving(false);
      }
    }
  };
  
  // Handle final completion after viewing summary
  const handleCompleteSummary = () => {
    if (calculatedGoals) {
      onComplete({
        ...calculatedGoals,
        locked: false,
        intake_complete: true,
        miq_impact: `🎉 Assessment Complete! Your MIQ Confidence increased by ${calculatedGoals.miq_confidence_boost || 30}%. Now let's boost it further with metabolic analysis and meal logging.`
      });
    }
  };
  
  // Helper to get readable label for intake values
  const getReadableLabel = (key, value) => {
    const question = allQuestions.find(q => q.key === key);
    if (!question || !value) return null;
    
    // Handle multi-select arrays
    if (Array.isArray(value)) {
      if (value.length === 0) return null;
      const labels = value.map(v => {
        // For grouped questions, search in all groups
        if (question.groups) {
          for (const group of question.groups) {
            const option = group.options.find(o => o.value === v);
            if (option) return option.label;
          }
        }
        // For flat options
        const option = question.options?.find(o => o.value === v);
        return option?.label || v;
      }).filter(Boolean);
      
      if (labels.length <= 2) {
        return labels.join(', ');
      } else {
        return `${labels[0]}, ${labels[1]} +${labels.length - 2} more`;
      }
    }
    
    // Handle single select
    // For grouped questions, search in all groups
    if (question.groups) {
      for (const group of question.groups) {
        const option = group.options.find(o => o.value === value);
        if (option) return option.label;
      }
    }
    // For flat options
    const option = question.options?.find(o => o.value === value);
    return option?.label || value;
  };
  
  const handleSkip = () => {
    if (currentQuestion < activeQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      handleNext();
    }
  };
  
  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };
  
  if (!currentConfig) return null;
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // COMPLETION SUMMARY SCREEN
  // Shows updated goals and summarizes all answers
  // ═══════════════════════════════════════════════════════════════════════════════
  if (showSummary && calculatedGoals) {
    const targets = calculatedGoals.nutrition_goals?.daily_targets;
    const answeredQuestions = activeQuestions.filter(q => intakeData[q.key]);
    
    return (
      <div>
        {/* Success Header */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <span style={{ fontSize: '48px', display: 'block', marginBottom: '12px' }}>🎯</span>
          <h2 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '700', color: '#4CAF50' }}>
            SmartGoals Established!
          </h2>
          <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>
            Your personalized nutrition targets are ready
          </p>
        </div>
        
        {/* Updated Targets Card */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(139, 195, 74, 0.15) 100%)',
          border: '1px solid rgba(76, 175, 80, 0.3)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px'
        }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#4CAF50' }}>
            📊 Your Daily Targets
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div style={{ textAlign: 'center', padding: '12px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: 'white' }}>
                {targets?.calories || '—'}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Calories</div>
            </div>
            <div style={{ textAlign: 'center', padding: '12px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#81C784' }}>
                {targets?.protein_g || '—'}g
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Protein</div>
            </div>
            <div style={{ textAlign: 'center', padding: '12px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#FFD54F' }}>
                {targets?.carbs_g || '—'}g
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Carbs</div>
            </div>
            <div style={{ textAlign: 'center', padding: '12px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#FF8A65' }}>
                {targets?.fat_g || '—'}g
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Fat</div>
            </div>
          </div>
        </div>
        
        {/* Your Answers Summary */}
        <div style={{
          background: 'rgba(102, 126, 234, 0.1)',
          border: '1px solid rgba(102, 126, 234, 0.2)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px'
        }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#667eea' }}>
            🧠 Your Profile Summary
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {answeredQuestions.slice(0, 6).map(q => (
              <div key={q.key} style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                fontSize: '12px',
                padding: '6px 0',
                borderBottom: '1px solid rgba(255,255,255,0.05)'
              }}>
                <span style={{ color: 'var(--text-secondary)' }}>{q.question.split('?')[0]}:</span>
                <span style={{ color: 'white', fontWeight: '500' }}>
                  {getReadableLabel(q.key, intakeData[q.key])}
                </span>
              </div>
            ))}
            {answeredQuestions.length > 6 && (
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textAlign: 'center' }}>
                +{answeredQuestions.length - 6} more factors captured
              </div>
            )}
          </div>
        </div>
        
        {/* MIQ Confidence Note */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
          border: '1px solid rgba(102, 126, 234, 0.3)',
          borderRadius: '10px',
          padding: '12px',
          marginBottom: '20px'
        }}>
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            <strong style={{ color: '#667eea' }}>✨ MIQ Intelligence Boost</strong><br/>
            Your answers are now part of your baseline MIQ profile. As you log meals and complete daily check-ins, 
            MIQ will refine these targets with increasing confidence. Your nutrition intelligence will only get smarter!
          </p>
        </div>
        
        {/* Continue Button */}
        <button
          onClick={handleCompleteSummary}
          style={{
            width: '100%',
            padding: '14px',
            background: 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)',
            border: 'none',
            borderRadius: '10px',
            color: 'white',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
          }}
        >
          Start Tracking with SmartGoals 🚀
        </button>
      </div>
    );
  }
  
  return (
    <div>
      {/* Header explaining MIQ intake purpose */}
      <div style={{ 
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
        borderRadius: '10px',
        padding: '12px',
        marginBottom: '16px',
        border: '1px solid rgba(102, 126, 234, 0.2)'
      }}>
        <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          <strong style={{ color: '#667eea' }}>🧠 {isEditMode ? 'Edit SmartGoals' : 'SmartGoals Setup'}</strong><br/>
          {isEditMode 
            ? 'Your previous answers are pre-filled. Change any field you like — no need to re-enter everything.'
            : 'We\'re verifying your inputs and gathering more intelligence for smarter goals. Skip any question to continue.'
          }
        </p>
      </div>
      
      {/* Progress - shows answered vs current vs unanswered */}
      <div style={{ display: 'flex', gap: '3px', marginBottom: '20px' }}>
        {activeQuestions.map((q, i) => {
          const hasAnswer = intakeData[q.key] !== null && intakeData[q.key] !== undefined;
          const isCurrent = i === currentQuestion;
          const isPast = i < currentQuestion;
          
          return (
            <div 
              key={i}
              onClick={() => setCurrentQuestion(i)}
              style={{
                flex: 1,
                height: '4px',
                borderRadius: '2px',
                cursor: 'pointer',
                background: isCurrent 
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                  : hasAnswer 
                    ? '#4CAF50' // Green for answered
                    : isPast 
                      ? 'rgba(255,255,255,0.3)' // Lighter for skipped
                      : 'rgba(255,255,255,0.1)', // Dark for future
                transition: 'all 0.2s ease'
              }}
              title={hasAnswer ? `${q.question.split('?')[0]} ✓` : q.question.split('?')[0]}
            />
          );
        })}
      </div>
      
      <p style={{ margin: '0 0 8px 0', fontSize: '11px', color: 'var(--text-secondary)' }}>
        Question {currentQuestion + 1} of {activeQuestions.length}
        {currentConfig.optional && <span style={{ marginLeft: '8px', color: '#ff9800' }}>(Optional)</span>}
        {currentConfig.multiSelect && <span style={{ marginLeft: '8px', color: '#667eea' }}>✓ Select all that apply</span>}
        {intakeData[currentKey] && <span style={{ marginLeft: '8px', color: '#4CAF50' }}>✓ Answered</span>}
      </p>
      
      {/* Premium Badge for Premium Features */}
      {currentConfig.premiumBadge && (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '4px 10px',
          background: 'linear-gradient(135deg, rgba(56, 239, 125, 0.15) 0%, rgba(17, 153, 142, 0.1) 100%)',
          border: '1px solid rgba(56, 239, 125, 0.3)',
          borderRadius: '6px',
          marginBottom: '8px'
        }}>
          <span style={{ fontSize: '11px', color: '#38ef7d', fontWeight: '600' }}>
            {currentConfig.premiumBadge}
          </span>
          <span style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Premium Feature</span>
        </div>
      )}
      
      <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>
        {currentConfig.question}
      </h3>
      
      {currentConfig.helper && (
        <p style={{ margin: '0 0 16px 0', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
          {currentConfig.helper}
        </p>
      )}
      
      {/* Educational Copy for Premium Features */}
      {currentConfig.educationalCopy && (
        <div style={{
          margin: '0 0 16px 0',
          padding: '12px',
          background: 'linear-gradient(135deg, rgba(56, 239, 125, 0.08) 0%, rgba(17, 153, 142, 0.05) 100%)',
          border: '1px solid rgba(56, 239, 125, 0.2)',
          borderRadius: '8px'
        }}>
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            💡 <span style={{ color: '#38ef7d', fontWeight: '500' }}>Why this helps:</span> {currentConfig.educationalCopy}
          </p>
        </div>
      )}
      
      {/* Secondary info / tooltip for ancestry */}
      {currentConfig.secondaryInfo && (
        <p style={{ 
          margin: '0 0 16px 0', 
          fontSize: '11px', 
          color: '#667eea', 
          fontStyle: 'italic',
          padding: '8px 10px',
          background: 'rgba(102, 126, 234, 0.08)',
          borderRadius: '6px'
        }}>
          ℹ️ {currentConfig.secondaryInfo}
        </p>
      )}
      
      {/* Number Input Type (for target weight) */}
      {currentConfig.inputType === 'number' && (
        <div style={{ marginBottom: '20px' }}>
          {/* Determine the weight unit - use local toggle state */}
          {(() => {
            const isKg = !useImperialLocal;
            const displayUnit = isKg ? 'kg' : 'lbs';
            const minWeight = isKg ? 30 : 80;
            const maxWeight = isKg ? 200 : 400;
            
            // Get current weight - backend stores as weight_kg, convert if needed
            const currentWeightKg = userProfile?.weight_kg || userProfile?.weight;
            const currentWeight = currentWeightKg 
              ? (isKg ? Math.round(currentWeightKg) : Math.round(currentWeightKg * 2.205)) 
              : null;
            
            return (
              <>
                {/* Unit Toggle */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '16px'
                }}>
                  <button
                    type="button"
                    onClick={() => {
                      // Clear target weight when switching units to avoid confusion
                      setIntakeData(prev => ({ ...prev, target_weight: null }));
                      setUseImperialLocal(false);
                    }}
                    style={{
                      padding: '8px 20px',
                      borderRadius: '20px',
                      border: !useImperialLocal ? '2px solid #667eea' : '1px solid rgba(255,255,255,0.2)',
                      background: !useImperialLocal ? 'rgba(102, 126, 234, 0.15)' : 'transparent',
                      color: !useImperialLocal ? '#667eea' : 'var(--text-secondary)',
                      fontSize: '14px',
                      fontWeight: !useImperialLocal ? '600' : '400',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Metric (kg)
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      // Clear target weight when switching units to avoid confusion
                      setIntakeData(prev => ({ ...prev, target_weight: null }));
                      setUseImperialLocal(true);
                    }}
                    style={{
                      padding: '8px 20px',
                      borderRadius: '20px',
                      border: useImperialLocal ? '2px solid #667eea' : '1px solid rgba(255,255,255,0.2)',
                      background: useImperialLocal ? 'rgba(102, 126, 234, 0.15)' : 'transparent',
                      color: useImperialLocal ? '#667eea' : 'var(--text-secondary)',
                      fontSize: '14px',
                      fontWeight: useImperialLocal ? '600' : '400',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Imperial (lbs)
                  </button>
                </div>
                
                {/* Show current weight if available */}
                {currentConfig.showCurrentWeight && currentWeight && (
                  <div style={{
                    padding: '12px',
                    background: 'rgba(102, 126, 234, 0.1)',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                      Current weight:
                    </span>
                    <span style={{ fontSize: '16px', fontWeight: '600', color: '#667eea' }}>
                      {currentWeight} {displayUnit}
                    </span>
                  </div>
                )}
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input
                    type="number"
                    value={intakeData[currentKey] || ''}
                    onChange={(e) => {
                      const inputVal = e.target.value;
                      if (inputVal === '') {
                        setIntakeData(prev => ({ ...prev, [currentKey]: '' }));
                      } else {
                        const val = parseFloat(inputVal);
                        if (!isNaN(val)) {
                          setIntakeData(prev => ({ ...prev, [currentKey]: val }));
                        }
                      }
                    }}
                    placeholder={currentConfig.inputLabel}
                    min={minWeight}
                    max={maxWeight}
                    style={{
                      flex: 1,
                      padding: '14px 16px',
                      borderRadius: '10px',
                      border: '1px solid rgba(255,255,255,0.15)',
                      background: 'rgba(255,255,255,0.03)',
                      color: 'inherit',
                      fontSize: '18px',
                      fontWeight: '600',
                      fontFamily: 'inherit'
                    }}
                  />
                  <span style={{ 
                    fontSize: '14px', 
                    color: 'var(--text-secondary)',
                    fontWeight: '500'
                  }}>
                    {displayUnit}
                  </span>
                </div>
              </>
            );
          })()}
          
          {/* Weight change indicator */}
          {(() => {
            const isKg = !useImperialLocal;
            const displayUnit = isKg ? 'kg' : 'lbs';
            const currentWeightKg = userProfile?.weight_kg || userProfile?.weight;
            const currentWeight = currentWeightKg 
              ? (isKg ? Math.round(currentWeightKg) : Math.round(currentWeightKg * 2.205)) 
              : null;
            
            if (!intakeData[currentKey] || !currentWeight) return null;
            
            const targetWeight = intakeData[currentKey];
            const diff = Math.abs(currentWeight - targetWeight).toFixed(1);
            const isLoss = targetWeight < currentWeight;
            const isGain = targetWeight > currentWeight;
            
            return (
              <div style={{
                marginTop: '12px',
                padding: '10px 12px',
                background: isLoss 
                  ? 'rgba(56, 239, 125, 0.1)' 
                  : isGain 
                    ? 'rgba(102, 126, 234, 0.1)'
                    : 'rgba(255, 255, 255, 0.05)',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '16px' }}>
                  {isLoss ? '📉' : isGain ? '📈' : '⚖️'}
                </span>
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                  {isLoss 
                    ? `Lose ${diff} ${displayUnit}`
                    : isGain
                      ? `Gain ${diff} ${displayUnit}`
                      : 'Maintain current weight'}
                </span>
              </div>
            );
          })()}
        </div>
      )}
      
      {/* Options - Grouped (for ancestry) or Flat */}
      {(currentConfig.options || currentConfig.groups) && (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '8px', 
          marginBottom: '20px',
          maxHeight: '320px',
          overflowY: 'auto'
        }}>
        {/* Prominent "No Allergies" button for food_allergies question */}
        {currentConfig.hasNoneOption && (
          <button
            data-testid="no-allergies-btn"
            onClick={() => handleSelect('none')}
            style={{
              padding: '14px 16px',
              borderRadius: '10px',
              border: (intakeData[currentKey] || []).includes('none')
                ? '2px solid #38ef7d'
                : '2px solid rgba(56, 239, 125, 0.3)',
              background: (intakeData[currentKey] || []).includes('none')
                ? 'rgba(56, 239, 125, 0.15)'
                : 'rgba(56, 239, 125, 0.05)',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: '600',
              color: (intakeData[currentKey] || []).includes('none') ? '#38ef7d' : 'var(--text-secondary)',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              marginBottom: '8px'
            }}
          >
            {(intakeData[currentKey] || []).includes('none') ? '✓ ' : ''}No Allergies or Intolerances
          </button>
        )}
        {currentConfig.grouped && currentConfig.groups ? (
          // Grouped options (ancestry)
          currentConfig.groups.map((group) => (
            <div key={group.label} style={{ marginBottom: '8px' }}>
              <p style={{ 
                margin: '0 0 6px 0', 
                fontSize: '11px', 
                fontWeight: '600', 
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {group.label}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {group.options.map((opt) => {
                  const isSelected = currentConfig.multiSelect 
                    ? (intakeData[currentKey] || []).includes(opt.value)
                    : intakeData[currentKey] === opt.value;
                  
                  return (
                    <button
                      key={opt.value}
                      onClick={() => handleSelect(opt.value)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: isSelected 
                          ? '2px solid #667eea' 
                          : '1px solid rgba(255,255,255,0.15)',
                        background: isSelected 
                          ? 'rgba(102, 126, 234, 0.15)' 
                          : 'rgba(255,255,255,0.03)',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: isSelected ? '600' : '400',
                        transition: 'all 0.2s',
                        color: 'inherit',
                        position: 'relative'
                      }}
                    >
                      {currentConfig.multiSelect && isSelected && (
                        <span style={{ 
                          marginRight: '6px', 
                          color: '#667eea',
                          fontWeight: '700'
                        }}>
                          ✓
                        </span>
                      )}
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))
        ) : (
          // Flat options (default)
          currentConfig.options?.map((opt) => {
            const isSelected = currentConfig.multiSelect 
              ? (intakeData[currentKey] || []).includes(opt.value)
              : intakeData[currentKey] === opt.value;
            
            return (
              <button
                key={opt.value}
                onClick={() => handleSelect(opt.value)}
                style={{
                  padding: '12px 14px',
                  borderRadius: '10px',
                  border: isSelected 
                    ? '2px solid #667eea' 
                    : '1px solid rgba(255,255,255,0.15)',
                  background: isSelected 
                    ? 'rgba(102, 126, 234, 0.15)' 
                    : 'rgba(255,255,255,0.03)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {currentConfig.multiSelect && isSelected && (
                    <span style={{ 
                      color: '#667eea',
                      fontWeight: '700',
                      fontSize: '14px'
                    }}>
                      ✓
                    </span>
                  )}
                  <div>
                    <span style={{ fontWeight: '500', fontSize: '14px' }}>{opt.label}</span>
                    {opt.description && (
                      <span style={{ display: 'block', fontSize: '11px', color: 'var(--text-secondary)', marginTop: '3px' }}>
                        {opt.description}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })
        )}
        </div>
      )}

      {/* Additional text inputs for injuries/other questions that support them */}
      {currentConfig.additionalInputs && (intakeData[currentKey] || []).length > 0 && 
       !(intakeData[currentKey] || []).includes('none') && (
        <div style={{ marginTop: '12px' }}>
          {currentConfig.additionalInputs.map((input) => (
            <div key={input.key} style={{ marginBottom: '10px' }}>
              <label style={{ 
                display: 'block', fontSize: '11px', fontWeight: '600', 
                color: 'var(--text-secondary)', marginBottom: '6px',
                textTransform: 'uppercase', letterSpacing: '0.5px'
              }}>
                {input.label}
              </label>
              {input.type === 'textarea' ? (
                <textarea
                  data-testid={`input-${input.key}`}
                  value={intakeData[input.key] || ''}
                  onChange={(e) => setIntakeData({ ...intakeData, [input.key]: e.target.value })}
                  placeholder={input.placeholder}
                  rows={3}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.15)',
                    background: 'rgba(255,255,255,0.04)',
                    color: 'var(--text-primary)', fontSize: '13px',
                    resize: 'vertical', fontFamily: 'inherit',
                    outline: 'none', boxSizing: 'border-box'
                  }}
                />
              ) : (
                <input
                  data-testid={`input-${input.key}`}
                  type="text"
                  value={intakeData[input.key] || ''}
                  onChange={(e) => setIntakeData({ ...intakeData, [input.key]: e.target.value })}
                  placeholder={input.placeholder}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.15)',
                    background: 'rgba(255,255,255,0.04)',
                    color: 'var(--text-primary)', fontSize: '13px',
                    fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box'
                  }}
                />
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* Custom Meals Stepper - shows when meals_per_day is 'custom' */}
      {currentKey === 'meals_per_day' && intakeData.meals_per_day === 'custom' && (
        <div style={{
          background: 'rgba(102, 126, 234, 0.1)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px',
          border: '1px solid rgba(102, 126, 234, 0.2)'
        }}>
          <p style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: '500', color: 'white' }}>
            Specify exact meals per day:
          </p>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
            <button
              onClick={() => {
                const current = intakeData.custom_meals_count || 6;
                if (current > 6) {
                  setIntakeData({ ...intakeData, custom_meals_count: current - 1 });
                }
              }}
              disabled={(intakeData.custom_meals_count || 6) <= 6}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                border: 'none',
                background: (intakeData.custom_meals_count || 6) <= 6 
                  ? 'rgba(255,255,255,0.1)' 
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontSize: '24px',
                cursor: (intakeData.custom_meals_count || 6) <= 6 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: (intakeData.custom_meals_count || 6) <= 6 ? 0.5 : 1
              }}
            >
              −
            </button>
            <div style={{ textAlign: 'center' }}>
              <span style={{ 
                fontSize: '36px', 
                fontWeight: '700', 
                color: '#667eea',
                display: 'block',
                lineHeight: 1
              }}>
                {intakeData.custom_meals_count || 6}
              </span>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                meals/day
              </span>
            </div>
            <button
              onClick={() => {
                const current = intakeData.custom_meals_count || 6;
                if (current < 8) {
                  setIntakeData({ ...intakeData, custom_meals_count: current + 1 });
                }
              }}
              disabled={(intakeData.custom_meals_count || 6) >= 8}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                border: 'none',
                background: (intakeData.custom_meals_count || 6) >= 8 
                  ? 'rgba(255,255,255,0.1)' 
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontSize: '24px',
                cursor: (intakeData.custom_meals_count || 6) >= 8 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: (intakeData.custom_meals_count || 6) >= 8 ? 0.5 : 1
              }}
            >
              +
            </button>
          </div>
          <p style={{ 
            margin: '12px 0 0 0', 
            fontSize: '11px', 
            color: 'var(--text-secondary)', 
            textAlign: 'center',
            lineHeight: 1.4
          }}>
            {(intakeData.custom_meals_count || 6) > 5 && (
              <>
                <strong style={{ color: '#ff9800' }}>Light Meals:</strong> Meals 6-{intakeData.custom_meals_count || 6} will be 
                smaller-calorie portions optimized for grazing
              </>
            )}
          </p>
        </div>
      )}
      
      {/* Custom Timeline Input - shows when goal_timeline_weeks is 'custom' */}
      {currentKey === 'goal_timeline_weeks' && intakeData.goal_timeline_weeks === 'custom' && (
        <div style={{
          background: 'rgba(102, 126, 234, 0.1)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px',
          border: '1px solid rgba(102, 126, 234, 0.2)'
        }}>
          <p style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: '500', color: 'white' }}>
            Enter your goal timeline (weeks):
          </p>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
            <button
              onClick={() => {
                const current = intakeData.custom_timeline_weeks || 12;
                if (current > 1) {
                  setIntakeData({ ...intakeData, custom_timeline_weeks: current - 1 });
                }
              }}
              disabled={(intakeData.custom_timeline_weeks || 12) <= 1}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                border: 'none',
                background: (intakeData.custom_timeline_weeks || 12) <= 1 
                  ? 'rgba(255,255,255,0.1)' 
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontSize: '24px',
                cursor: (intakeData.custom_timeline_weeks || 12) <= 1 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: (intakeData.custom_timeline_weeks || 12) <= 1 ? 0.5 : 1
              }}
            >
              −
            </button>
            <div style={{ textAlign: 'center' }}>
              <span style={{ 
                fontSize: '36px', 
                fontWeight: '700', 
                color: '#667eea',
                display: 'block',
                lineHeight: 1
              }}>
                {intakeData.custom_timeline_weeks || 12}
              </span>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                weeks
              </span>
            </div>
            <button
              onClick={() => {
                const current = intakeData.custom_timeline_weeks || 12;
                if (current < 52) {
                  setIntakeData({ ...intakeData, custom_timeline_weeks: current + 1 });
                }
              }}
              disabled={(intakeData.custom_timeline_weeks || 12) >= 52}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                border: 'none',
                background: (intakeData.custom_timeline_weeks || 12) >= 52 
                  ? 'rgba(255,255,255,0.1)' 
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontSize: '24px',
                cursor: (intakeData.custom_timeline_weeks || 12) >= 52 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: (intakeData.custom_timeline_weeks || 12) >= 52 ? 0.5 : 1
              }}
            >
              +
            </button>
          </div>
          <p style={{ 
            margin: '12px 0 0 0', 
            fontSize: '11px', 
            color: 'var(--text-secondary)', 
            textAlign: 'center',
            lineHeight: 1.4
          }}>
            {(intakeData.custom_timeline_weeks || 12) <= 4 && (
              <span style={{ color: '#ff9800' }}>
                ⚡ Aggressive timeline - requires strong discipline
              </span>
            )}
            {(intakeData.custom_timeline_weeks || 12) > 4 && (intakeData.custom_timeline_weeks || 12) <= 12 && (
              <span style={{ color: '#38ef7d' }}>
                ✓ Balanced timeline - sustainable results
              </span>
            )}
            {(intakeData.custom_timeline_weeks || 12) > 12 && (
              <span style={{ color: '#667eea' }}>
                🌿 Gradual timeline - very maintainable
              </span>
            )}
          </p>
        </div>
      )}
      
      {/* Actions */}
      <div style={{ display: 'flex', gap: '10px', marginTop: '8px', position: 'relative', zIndex: 10 }}>
        {currentQuestion > 0 && (
          <button 
            onClick={handleBack}
            className="btn-secondary"
            style={{ 
              padding: '14px 20px', 
              minHeight: '50px',
              fontSize: '16px',
              borderRadius: '10px',
              cursor: 'pointer'
            }}
          >
            ←
          </button>
        )}
        <button 
          onClick={handleSkip}
          className="btn-secondary"
          style={{ 
            flex: 1, 
            padding: '14px 16px', 
            minHeight: '50px',
            fontSize: '14px',
            fontWeight: '500',
            borderRadius: '10px',
            cursor: 'pointer'
          }}
          disabled={saving}
        >
          {currentConfig.optional ? 'Skip' : 'Skip'}
        </button>
        <button 
          onClick={handleNext}
          className="btn-primary"
          style={{ 
            flex: 2, 
            padding: '14px 20px', 
            minHeight: '50px',
            fontSize: '15px',
            fontWeight: '600',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
            cursor: 'pointer'
          }}
          disabled={(() => {
            if (saving) return true;
            if (currentConfig.optional) return false;
            if (currentConfig.inputType === 'number') {
              // For number inputs, allow if a valid number is entered
              const val = intakeData[currentKey];
              return val === null || val === undefined || val === '';
            }
            // For regular options
            return !intakeData[currentKey];
          })()}
        >
          {saving ? 'Saving...' : currentQuestion === activeQuestions.length - 1 ? '✨ Complete' : 'Next →'}
        </button>
      </div>
    </div>
  );
};

export default MiqIntakeForm;
